import React, { useMemo } from "react";
import { LeaderboardSectionData, LeaderboardTheme } from "../../types/home.types";
import {
  DEFAULT_THEME,
  getInitials,
  rankEntries,
  RankedEntry,
  withAlpha,
} from "./leaderboard.utils";

export type LeaderboardSectionProps = Partial<
  Pick<
    LeaderboardSectionData,
    "title" | "subtitle" | "logoUrl" | "showPoints" | "showEmail" | "topN" | "theme" | "entries"
  >
> & {
  /** Fixed height instead of filling the grid cell, for previews. */
  compact?: boolean;
};

const PODIUM_ORDER = [1, 0, 2];

function podiumGradient(theme: LeaderboardTheme, rank: number): string {
  const [from, to] =
    rank === 1
      ? [theme.podiumFirstFrom, theme.podiumFirstTo]
      : rank === 2
        ? [theme.podiumSecondFrom, theme.podiumSecondTo]
        : [theme.podiumThirdFrom, theme.podiumThirdTo];
  return `linear-gradient(135deg, ${from}, ${to})`;
}

const formatPoints = (points: number) => points.toLocaleString("en-US");

const PodiumPlace: React.FC<{
  entry: RankedEntry;
  isCenter: boolean;
  showPoints: boolean;
  showEmail: boolean;
  theme: LeaderboardTheme;
}> = ({ entry, isCenter, showPoints, showEmail, theme }) => {
  const avatarSize = isCenter ? 72 : 56;
  const width = isCenter ? 100 : 88;

  return (
    <div
      className="flex flex-col items-center gap-1 min-w-0"
      style={{ width, marginBottom: isCenter ? 10 : 0 }}
    >
      <div className="relative mb-1">
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: avatarSize,
            height: avatarSize,
            background: podiumGradient(theme, entry.rank),
            fontSize: isCenter ? 22 : 17,
            fontWeight: 800,
            color: "#FFFFFF",
            border: `3px solid ${withAlpha(theme.text, 0.15)}`,
          }}
        >
          {getInitials(entry.name)}
        </div>
        <div
          className="absolute flex items-center justify-center rounded-full"
          style={{
            top: -4,
            left: -4,
            width: 22,
            height: 22,
            background: entry.rank === 1 ? theme.badgeFirst : theme.badgeOther,
            color: entry.rank === 1 ? theme.badgeFirstText : "#FFFFFF",
            border: `2px solid ${theme.background}`,
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          {entry.rank}
        </div>
      </div>
      <div
        className="text-center truncate w-full"
        title={entry.name}
        style={{ fontSize: 14, fontWeight: 700, color: withAlpha(theme.text, 0.75) }}
      >
        {entry.name}
      </div>
      {showEmail && entry.email && (
        <div
          className="text-center truncate w-full"
          title={entry.email}
          style={{ fontSize: 11, color: withAlpha(theme.text, 0.5) }}
        >
          {entry.email}
        </div>
      )}
      {showPoints && (
        <div style={{ fontSize: 16, fontWeight: 600, color: theme.points }}>
          {formatPoints(entry.points)}
        </div>
      )}
    </div>
  );
};

const LeaderboardSection: React.FC<LeaderboardSectionProps> = ({
  title = "",
  subtitle = "",
  logoUrl,
  showPoints = true,
  showEmail = false,
  topN = 10,
  theme: themeProp,
  entries = [],
  compact = false,
}) => {
  const theme = { ...DEFAULT_THEME, ...themeProp };
  const ranked = useMemo(() => rankEntries(entries).slice(0, Math.max(3, topN)), [entries, topN]);
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <div
      className={`shadow-sm rounded-xl overflow-hidden flex flex-col font-madefor ${
        compact ? "h-[520px]" : "h-full min-h-[542px]"
      }`}
      style={{ background: theme.background, color: theme.text }}
    >
      <div className="shrink-0" style={{ padding: "20px 22px 0" }}>
        {logoUrl && (
          <div className="flex justify-center mb-3">
            <img src={logoUrl} alt="" style={{ width: 120 }} className="h-auto object-contain" />
          </div>
        )}
        <div className="text-center mb-4">
          {title && (
            <div
              className="font-petrona"
              style={{ fontSize: 26, fontWeight: 300, color: theme.title, marginBottom: 2 }}
            >
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: 16, color: withAlpha(theme.title, 0.75) }}>{subtitle}</div>
          )}
        </div>
      </div>

      {ranked.length === 0 ? (
        <div
          className="flex-1 flex items-center justify-center text-center px-6 pb-6"
          style={{ color: withAlpha(theme.text, 0.6) }}
        >
          The leaderboard will appear here soon.
        </div>
      ) : (
        <>
          <div
            className="shrink-0 flex justify-center items-end mb-4 px-4"
            style={{ gap: 12 }}
          >
            {PODIUM_ORDER.map((position) => {
              const entry = podium[position];
              if (!entry) return null;
              return (
                <PodiumPlace
                  key={position}
                  entry={entry}
                  isCenter={position === 0}
                  showPoints={showPoints}
                  showEmail={showEmail}
                  theme={theme}
                />
              );
            })}
          </div>

          {/* Absolutely positioned so a long list scrolls instead of stretching the grid row. */}
          <div className="relative flex-1 min-h-[120px]">
            <div className="absolute inset-0 overflow-y-auto px-4 pb-4">
              {rest.map((entry, index) => (
                <div
                  key={`${entry.name}-${index}`}
                  className="flex items-center"
                  style={{
                    gap: 12,
                    padding: "12px 14px",
                    marginBottom: 4,
                    borderRadius: 8,
                    background: withAlpha(theme.text, 0.03),
                  }}
                >
                  <div
                    className="font-petrona text-center shrink-0"
                    style={{ width: 24, fontSize: 17, fontWeight: 300, color: withAlpha(theme.text, 0.55) }}
                  >
                    {entry.rank}
                  </div>
                  <div
                    className="flex items-center justify-center rounded-full shrink-0"
                    style={{
                      width: 36,
                      height: 36,
                      background: withAlpha(theme.accent, 0.15),
                      color: theme.accent,
                      fontSize: 13,
                      fontWeight: 800,
                    }}
                  >
                    {getInitials(entry.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="truncate"
                      title={entry.name}
                      style={{ fontSize: 16, fontWeight: 700, color: theme.text }}
                    >
                      {entry.name}
                    </div>
                    {showEmail && entry.email && (
                      <div
                        className="truncate"
                        title={entry.email}
                        style={{ fontSize: 12, color: withAlpha(theme.text, 0.55) }}
                      >
                        {entry.email}
                      </div>
                    )}
                  </div>
                  {showPoints && (
                    <div
                      className="font-petrona shrink-0"
                      style={{ fontSize: 18, fontWeight: 600, color: theme.points }}
                    >
                      {formatPoints(entry.points)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LeaderboardSection;
